import React, { useContext } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { secureHttp } from '../../utils/request';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import type { TableColumn } from 'react-data-table-component';
import { formatAddress } from '../../utils/property';
import AppContext from '../../context/app_context';
import DataTable from '../../components/common/data_table';
import moment from 'moment';
import { fetchReviewOverviewData } from '../reviews/reviews_list_common';
import { Button } from '../../components/common/button';

type TFacilityReviewSnippetsOverview = {
    OrgSlug: string
    OrgName: string
    OrgNHID: number
    NHID: number
    PropertyName: string
    PropertyCity: string
    PropertyCounty: string
    SalesforceOwnerEmail: string
    SalesforceOwner: string
    StateCode: string
    PropertyAddressLine1: string
    PropertyZip: string
    ReviewShowcaseID: number
    ReviewID: number
    Snippet: string
    Sequence: number
    CreatedAt: string
    CreatedBy: string
    UpdatedAt: string | null,
    UpdatedBy: string | null,
    TotalRowCount: number,
    SnippetList: { Sequence: number, Snippet: string, UpdatedAt: string }[]
    TotalPublishedReviews: number
    PropertyCitySlug: string
    PropertyStateSlug: string
    PropertySlug: string
    PropertyWebsiteURL: string
};

export const buildOverviewColumns = (handleSelect: (NHID: number | null) => void) => {
    const appContext: any = useContext(AppContext);

    const cols: TableColumn<TFacilityReviewSnippetsOverview>[] = [
        {
            name: 'Facility Name',
            selector: (row) => row.PropertyName,
            cell: (row) => <div className="py-2">
                <div><strong>{row.PropertyName}</strong></div>
                <div>{formatAddress(row, ['Address1', 'PropertyCity', 'PropertyZip'])}</div>
                <div>
                    <a
                        className="link text-xs bg-brand_faint-blue px-2 py-1 inline-block leading-none rounded-lg relative -top-px"
                        href={`${row.PropertyWebsiteURL}/${row.PropertyStateSlug}/${row.PropertyCitySlug}/${row.PropertySlug}`}
                        target="_blank"
                    >
                        view on website
                    </a>
                </div>
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '270px',
            wrap: true,
        },
        {
            name: '# of Published Reviews',
            selector: (row) => row.TotalPublishedReviews,
            cell: (row) => <div className="py-2">
                <div>{row.TotalPublishedReviews}</div>
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '180px',
            wrap: true,
        },
        {
            name: '# of Showcase Reviews (maximum 5)',
            selector: (row) => row.SnippetList.length,
            cell: (row) => <div className="py-2">
                <div>{row.SnippetList.length}</div>
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '180px',
            wrap: true,
        },
        {
            name: 'Review Snippets',
            cell: (row) => {
                return <div className="flex flex-col align-center gap-2 py-2">
                    {row.SnippetList.map((snippet, index) => 
                        <div key={index}>{snippet.Sequence}. {snippet.Snippet}</div>
                    )}
                </div>;
            },
            sortable: false,
            wrap: true,
        },
        {
            name: 'Last Updated',
            selector: (row) => row.SnippetList.length ? Math.max(...row.SnippetList.map(e => moment(e.UpdatedAt).unix())) : 0,
            cell: (row) => {
                const latestUpdateUnixTime = row.SnippetList.length ? Math.max(...row.SnippetList.map(e => moment(e.UpdatedAt).unix())) : 0;

                return <div className="py-2">
                    <div>{latestUpdateUnixTime > 0 ? moment.unix(latestUpdateUnixTime).format('Do MMM YYYY') : ''}</div>
                </div>;
            },
            sortable: true,
            width: appContext.isMobile ? '180px' : '180px',
            wrap: true,
        },
        {
            name: '',
            cell: (row) => <div className="py-2" style={{ display: 'flex', flexDirection: 'column' }}>
                <Button 
                    // className="btn"
                    onClick={() => handleSelect(row.NHID)}
                    disabled={!row.TotalPublishedReviews}
                >
                    {row.SnippetList.length === 0 ? 'Add' : row.SnippetList.length === 5 ? 'Edit' : 'Add/Edit'}
                </Button>
            </div>,
            sortable: false,
            width: appContext.isMobile ? '180px' : '180px',
        },
    ];

    return cols;
};

const ReviewSnippetsMultiFacilityTable = ({ 
    handleSelect 
}: { 
    handleSelect: (NHID: number | null) => void 
}) => { 

    const { Org, Properties } = useSelector(
        ({ SessionState }: RootState) => SessionState
    );

    const orgNHID = Org?.NHID || Properties[0].OrgNHID;

    const showcaseQueryKey = ['review', 'showcase', orgNHID];

    const queriesData = useQueries({ 
        queries: [{ 
            queryKey: showcaseQueryKey, 
            queryFn: async () => {
                const params = { OrgNHID: orgNHID };

                const { data } = await secureHttp.get('/reviewshowcase/', { params });

                return data;
            },
            select: (data: any) => {

                const snippetsData: TFacilityReviewSnippetsOverview[] = data?.data?.rows; 

                if (!snippetsData) {
                    return snippetsData;
                }

                const allProperties = Properties.map((item) => {
                    
                    const currentPropertySnippets = snippetsData.filter((snippetItem) => snippetItem.NHID === item.NHID);
                    
                    const snippets = currentPropertySnippets.map((snippetItem) => {
                        return {
                            Sequence: snippetItem.Sequence,
                            Snippet: snippetItem.Snippet,
                            UpdatedAt: snippetItem.UpdatedAt ?? snippetItem.CreatedAt
                        };
                    }).sort((a, b) => a.Sequence - b.Sequence);

                    return {
                        OrgName: item.OrgName,
                        OrgNHID: item.OrgNHID,
                        NHID: item.NHID,
                        Address1: item.Address1,
                        PropertyName: item.Name,
                        PropertyCity: item.City,
                        PropertyCitySlug: item.CitySlug,
                        PropertyStateSlug: item.StateSlug,
                        PropertySlug: item.Slug,
                        PropertyZip: item.PostCode,
                        SnippetList: snippets,
                        PropertyWebsiteURL: item.WebsiteURL
                    };
                });

                return allProperties;
            },
            enabled: !!orgNHID, 
        },
        { 
            queryKey: ['reviews', 'overview', orgNHID], 
            queryFn: async () => {
                const data = await fetchReviewOverviewData({});

                return data?.rows;
            },
            enabled: !!orgNHID, 
        }]
    });

    const showcaseArr = queriesData ? queriesData[0].data || [] : [];
    const reviewTotalsArr = queriesData ? queriesData[1].data || [] : [];

    const tableDataArr = showcaseArr.map((showcaseObj) => {
        
        const reviewStats = reviewTotalsArr.find(reviewTotalObj => reviewTotalObj.NHID === showcaseObj.NHID);
        
        return {
            ...showcaseObj,
            TotalPublishedReviews: reviewStats ? reviewStats.TotalReviewCount || 0 : 0
        };
    });

    const columns = buildOverviewColumns(handleSelect);

    return <div className="mt-4 sm:mt-6 md:mt-8 2xl:mt-10 px-0">
        <DataTable
            columns={columns}
            data={tableDataArr}
            pagination={true}
            fixedHeader={false}
            // noDataComponent={<NoReviewData loading={busy} />}
        /> 
    </div>;
};

export default ReviewSnippetsMultiFacilityTable;
